import type { DynaVault } from '../../../../api/DynaVaultsAPI';
import type {
  VaultAssetTypeFilter,
  VaultChainFilter,
} from 'structure/pv2/dynavault/types';
import { TOKENS } from 'utils/constants/tokens';

type FilterableVault = Pick<
  DynaVault,
  'asset' | 'chainId' | 'depositToken' | 'name' | 'symbol'
>;

const chainIdsByFilter: Record<Exclude<VaultChainFilter, 'all'>, number> = {
  base: 8453,
  ethereum: 1,
  arbitrum: 42161,
  bnb: 56,
};

export const matchesVaultChainFilter = (
  vault: Pick<DynaVault, 'chainId'>,
  filter: VaultChainFilter
): boolean => filter === 'all' || vault.chainId === chainIdsByFilter[filter];

const includesSymbol = (value: string, symbols: string[]): boolean =>
  symbols.some((symbol) => value.includes(symbol));

const normalizeAddress = (address: string): string => address.toLowerCase();

const assetAddressesByType: Record<
  Exclude<VaultAssetTypeFilter, 'all'>,
  Set<string>
> = {
  ethereum: new Set(
    [
      TOKENS.ETHEREUM.WETH.address,
      TOKENS.ARBITRUM.WETH.address,
      TOKENS.OPTIMISM.WETH.address,
      TOKENS.POLYGON.WETH.address,
      TOKENS.BASE_L2.WETH.address,
    ].map(normalizeAddress)
  ),
  bitcoin: new Set(
    [
      TOKENS.ETHEREUM.WBTC.address,
      TOKENS.ARBITRUM.WBTC.address,
      TOKENS.OPTIMISM.WBTC.address,
      TOKENS.POLYGON.WBTC.address,
      TOKENS.BNB.BTCB.address,
      TOKENS.BASE_L2.cbBTC.address,
    ].map(normalizeAddress)
  ),
  stable: new Set(
    [
      '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
      '0xdac17f958d2ee523a2206206994597c13d831ec7',
      TOKENS.ARBITRUM.USDC.address,
      TOKENS.ARBITRUM.USDT.address,
      TOKENS.BNB.USDC.address,
      TOKENS.BNB.USDT.address,
      TOKENS.BASE_L2.USDC.address,
      TOKENS.BASE_L2.USDT.address,
      TOKENS.BASE_L2.EURC.address,
    ].map(normalizeAddress)
  ),
};

export const getVaultAssetType = (
  vault: FilterableVault
): Exclude<VaultAssetTypeFilter, 'all'> | undefined => {
  const assetAddress = normalizeAddress(vault.depositToken || vault.asset);
  const addressMatch = Object.entries(assetAddressesByType).find(
    ([, addresses]) => addresses.has(assetAddress)
  )?.[0] as Exclude<VaultAssetTypeFilter, 'all'> | undefined;

  if (addressMatch) {
    return addressMatch;
  }

  const vaultIdentity = `${vault.symbol} ${vault.name}`
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

  if (includesSymbol(vaultIdentity, ['USDC', 'USDT', 'EURC', 'DAI'])) {
    return 'stable';
  }

  if (vaultIdentity.includes('BTC')) {
    return 'bitcoin';
  }

  if (includesSymbol(vaultIdentity, ['WETH', 'ETH'])) {
    return 'ethereum';
  }

  return undefined;
};

export const matchesVaultAssetTypeFilter = (
  vault: FilterableVault,
  filter: VaultAssetTypeFilter
): boolean => filter === 'all' || getVaultAssetType(vault) === filter;
